import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import AdminNav from "../components/AdminNav";
import { api, TOOL_LABEL, TOOL_COLOR } from "../lib/api";
import { toast } from "sonner";
import { Save, Undo2, Beaker, LogOut } from "lucide-react";

const PLACEHOLDERS = {
  "que-dibujo": ["{category}"],
  "pen-battle": ["{duration}"],
  "objeto-imposible": ["{duration}"],
};

const AdminTemplates = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingTool, setSavingTool] = useState(null);
  const [testTool, setTestTool] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const res = await api.get("/admin/templates");
      setRows(res.data.rows || []);
      const drafts = {};
      (res.data.rows || []).forEach((r) => { drafts[r.tool] = r.instruction; });
      setDrafts(drafts);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin");
      else toast.error("No se pudo cargar templates.");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (tool) => {
    setSavingTool(tool);
    try {
      await api.put(`/admin/templates/${tool}`, { instruction: drafts[tool] });
      toast.success("Guardado. La próxima generación lo usa.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando.");
    } finally {
      setSavingTool(null);
    }
  };

  const reset = async (tool) => {
    if (!confirm(`¿Restaurar prompt por defecto para ${TOOL_LABEL[tool]}?`)) return;
    setSavingTool(tool);
    try {
      await api.delete(`/admin/templates/${tool}`);
      toast.success("Restaurado al default.");
      await load();
    } catch {
      toast.error("Error.");
    } finally {
      setSavingTool(null);
    }
  };

  const test = async (tool) => {
    setTestTool(tool);
    setTestResult(null);
    try {
      const res = await api.post("/admin/templates/test", { instruction: drafts[tool], tool });
      setTestResult({ ...res.data, _tool: tool });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en test.");
    } finally {
      setTestTool(null);
    }
  };

  const logout = () => { localStorage.removeItem("guango_admin_token"); navigate("/admin"); };

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14 pb-24">
        <AdminNav />
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <h1 className="font-display font-black text-4xl sm:text-6xl tracking-tight">prompts ia.</h1>
          <button onClick={logout} className="press inline-flex items-center gap-2 bg-[#ef4444] text-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm">
            <LogOut className="w-4 h-4" strokeWidth={3} /> salir
          </button>
        </div>
        <p className="font-mono text-sm text-[#4a4a4a] mb-8 max-w-2xl">
          Edita el prompt que se le envía a Gemini en cada herramienta. Cambios se aplican inmediatamente. Usa "test" para probar antes de guardar.
        </p>

        {rows.map((row) => {
          const placeholders = PLACEHOLDERS[row.tool] || [];
          const draft = drafts[row.tool] ?? row.instruction;
          const dirty = draft !== row.instruction;
          const isCustom = row.is_custom;
          return (
            <div
              key={row.tool}
              data-testid={`tpl-${row.tool}`}
              className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-6 mb-6"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="inline-block font-mono text-xs uppercase tracking-wider font-bold border-2 border-[#1a1a1a] rounded-full px-3 py-1" style={{ background: TOOL_COLOR[row.tool] }}>
                    {TOOL_LABEL[row.tool]}
                  </span>
                  {isCustom ? (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#22c55e] font-bold">
                      ● custom · editado
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#737373]">
                      default
                    </span>
                  )}
                </div>
                {placeholders.length > 0 && (
                  <div className="font-mono text-[10px] text-[#737373]">
                    placeholders: {placeholders.join(", ")}
                  </div>
                )}
              </div>

              <textarea
                data-testid={`tpl-textarea-${row.tool}`}
                value={draft}
                onChange={(e) => setDrafts((d) => ({ ...d, [row.tool]: e.target.value }))}
                rows={6}
                className="w-full bg-[#f7f5f0] border-4 border-[#1a1a1a] rounded-xl px-3 py-2 font-mono text-xs leading-relaxed resize-y focus:bg-white"
              />

              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  data-testid={`tpl-save-${row.tool}`}
                  onClick={() => save(row.tool)}
                  disabled={!dirty || savingTool === row.tool}
                  className="press inline-flex items-center gap-2 bg-[#22c55e] border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase font-bold shadow-hard-sm disabled:opacity-40"
                >
                  <Save className="w-4 h-4" strokeWidth={3} />
                  {savingTool === row.tool ? "guardando..." : "guardar"}
                </button>
                <button
                  data-testid={`tpl-test-${row.tool}`}
                  onClick={() => test(row.tool)}
                  disabled={testTool === row.tool}
                  className="press inline-flex items-center gap-2 bg-[#FFB000] border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase font-bold shadow-hard-sm disabled:opacity-40"
                >
                  <Beaker className="w-4 h-4" strokeWidth={3} />
                  {testTool === row.tool ? "probando..." : "test"}
                </button>
                {isCustom && (
                  <button
                    data-testid={`tpl-reset-${row.tool}`}
                    onClick={() => reset(row.tool)}
                    className="press inline-flex items-center gap-2 bg-white border-4 border-[#1a1a1a] rounded-xl px-4 py-2 font-mono text-xs uppercase font-bold shadow-hard-sm"
                  >
                    <Undo2 className="w-4 h-4" strokeWidth={3} /> volver al default
                  </button>
                )}
              </div>

              {testTool === null && testResult && testResult._tool === row.tool && (
                <pre data-testid={`tpl-test-result-${row.tool}`} className="mt-4 bg-[#1a1a1a] text-[#22c55e] rounded-xl p-4 font-mono text-xs overflow-x-auto whitespace-pre-wrap break-words">
                  {JSON.stringify(testResult.parsed || { raw: testResult.raw }, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </section>
    </Layout>
  );
};

export default AdminTemplates;
